import React, {createContext, type Dispatch, type RefObject, type SetStateAction} from "react";
import {FitAddon} from "@xterm/addon-fit";
import {Terminal} from "@xterm/xterm";
import {resolveActionHandler} from "../../../components/InstanceActions.ts";
import {swal_util} from "../../../lib/SwalUtil.ts";
import {type NavigateFunction} from "react-router-dom";
import {type ISearchOptions, SearchAddon} from '@xterm/addon-search';
import {type TerminalSearchDialogRef} from "./TerminalSearchDialog.tsx";
import {WebglAddon} from "@xterm/addon-webgl";
import {WebLinksAddon} from "@xterm/addon-web-links";
import getThemeSet, {type ThemeSet} from "../util/ThemeSet.tsx";
import { t } from "i18next";

// ANSI escape code: Reverse Video Mode ["\x1b[?5h", "\x1b[?5l"];
// https://espterm.github.io/docs/VT100%20escape%20codes.html
// https://gist.github.com/delameter/b9772a0bf19032f977b985091f0eb5c1
const VISUAL_BELL_PATTERN = new RegExp(String.raw`\x1b\[\?5[hl]`, 'g');

export interface TerminalTab {
    isStdoutWrittenMoreThanOnce: boolean;
    reqKey: string;
    tabKey?: number;
    title: string;
    xterm: Terminal;
    xtermFitAddon: FitAddon;
    xtermSearchAddon: SearchAddon;
    webglAddon: WebglAddon;
    webLinksAddon: WebLinksAddon
    bounded: boolean;
}

export interface TerminalScreenEventHandlers {
    onResize?: (cols: number, rows: number) => void;

    // onChangeStatus(text: string, color?: string): void;
    onNewTab?: (newTab: TerminalTab) => void;

    onWriteStdIn?: (tabKey: number, data: string) => void;

    onCloseTab?: (tabKey: number) => void;

    onBell?(reqKey: string): void;

    onVisualBell?(tabKey: number, on: boolean): void;
}

export class TerminalScreenContainerStates {
    private readonly _eventHandlers: TerminalScreenEventHandlers;
    private readonly _lock: boolean;
    private readonly _setLock: Dispatch<SetStateAction<boolean>>;

    private readonly _tabs: TerminalTab[];
    private readonly _setTabs: Dispatch<React.SetStateAction<TerminalTab[]>>;

    private _activeTab: TerminalTab | undefined;
    private readonly _setActiveTab: Dispatch<SetStateAction<TerminalTab | undefined>>;

    constructor(
        eventHandlers: TerminalScreenEventHandlers,
        lockState: [boolean, Dispatch<SetStateAction<boolean>>],
        tabsState: [TerminalTab[], Dispatch<SetStateAction<TerminalTab[]>>],
        activeTabState: [TerminalTab | undefined, Dispatch<SetStateAction<TerminalTab | undefined>>],
    ) {
        this._eventHandlers = eventHandlers;
        [this._lock, this._setLock] = lockState;
        [this._tabs, this._setTabs] = tabsState;
        [this._activeTab, this._setActiveTab] = activeTabState;
    }

    get lock(): boolean {
        return this._lock;
    }

    set lock(value: boolean) {
        this._setLock(value);
    }

    get tabs(): TerminalTab[] {
        return this._tabs;
    }

    set tabs(value: TerminalTab[]) {
        this._setTabs(value);
    }

    get activeTab(): TerminalTab | undefined {
        return this._activeTab;
    }

    set activeTab(value: TerminalTab | undefined) {
        if (!value) {
            throw new Error(`activeTab is not set`);
        }
        this._setActiveTab(value);
        this._activeTab = value;
        console.log(`activeTab set: ${value.title}`);


        if (value.isStdoutWrittenMoreThanOnce) {
            this.fitActiveTerminal();
            return;
        }

        const intervalId = setInterval(() => {
            if (value.isStdoutWrittenMoreThanOnce) {
                clearInterval(intervalId);
                this.fitActiveTerminal();
            }
        }, 100);
    }

    private fitActiveTabDebounceTimer: number | undefined = undefined;

    fitActiveTerminal() {
        window.clearTimeout(this.fitActiveTabDebounceTimer);
        this.fitActiveTabDebounceTimer = window.setTimeout(() => {
            if (!this.activeTab) {
                return
            }
            const activeTab = this.activeTab;
            activeTab.xtermFitAddon.fit();
            activeTab.xterm.resize(activeTab.xterm.cols, activeTab.xterm.rows);


            if (this.tabs.length > 0 && this._eventHandlers.onResize) {
                this._eventHandlers.onResize(this.activeTab.xterm.cols, this.activeTab.xterm.rows);
            }

            this.fitActiveTabDebounceTimer = undefined;
        }, 100);
    }

}


export class TerminalScreenContainerContextImpl {
    private readonly _instanceId: number;
    private readonly _eventHandlers: TerminalScreenEventHandlers;
    private readonly _states: TerminalScreenContainerStates;
    private readonly _tabMap: Map<number, TerminalTab> = new Map<number, TerminalTab>();
    private readonly navigate: NavigateFunction;
    private readonly terminalSearchDialogRef: RefObject<TerminalSearchDialogRef>;
    private _themeSet: ThemeSet;

    constructor(
        navigate: NavigateFunction,
        instanceId: number,
        eventHandlers: TerminalScreenEventHandlers,
        terminalScreenContainerStates: TerminalScreenContainerStates,
        terminalSearchDialogRef: RefObject<TerminalSearchDialogRef>,
    ) {
        this.navigate = navigate;
        this._instanceId = instanceId;
        this._eventHandlers = eventHandlers;
        this._states = terminalScreenContainerStates;
        this.terminalSearchDialogRef = terminalSearchDialogRef;
        this._themeSet = getThemeSet("blank");
    }

    get lock(): boolean {
        return this._states.lock;
    }

    get activeTab(): TerminalTab | undefined {
        return this._states.activeTab;
    }

    set activeTab(value: TerminalTab | undefined) {
        if (value != this.activeTab) {
            this._states.activeTab = value;
        }
    }

    get tabs(): TerminalTab[] {
        return this._states.tabs;
    }

    get themeSet(): ThemeSet {
        return this._themeSet;
    }

    changeThemeSetFromOsImageName(value: string) {
        if (value.length == 0) {
            return;
        }
        const themeSet = getThemeSet(value, "ubuntu");
        if (this._themeSet == themeSet) return
        this._themeSet = themeSet;
        this._states.tabs.forEach(tab => {
            tab.xterm.options.theme = themeSet.defaultTheme;
        });
    }

    executeInstanceAction(action: string) {
        const actionFunc = resolveActionHandler(action)
        if (!actionFunc) {
            swal_util.show_error(`invalid action: ${action}`).finally();
            return;
        }

        actionFunc({
            instanceId: this._instanceId,
            tab: this._states.activeTab,
            closeTab: this._eventHandlers.onCloseTab,
            removeTaByReqKey: this.removeTaByReqKey
        }).finally(() => {
            if (!(action == "close"))
                this.navigate(0);
        });
    }

    newTab() {
        this._states.lock = true;

        const newTab: TerminalTab = {
            reqKey: crypto.randomUUID(),
            title: t("terminal.status.connectingShort"),
            xterm: new Terminal({
                allowProposedApi: true,
                cursorBlink: true,
                rightClickSelectsWord: true,
                fontFamily: 'Cascadia Mono, DejaVu Sans Mono, courier-new, courier, monospace',
                fontSize: 18,
                theme: this._themeSet.defaultTheme,
            }),
            xtermSearchAddon: new SearchAddon(),
            xtermFitAddon: new FitAddon(),
            webglAddon: new WebglAddon(),
            webLinksAddon: new WebLinksAddon(),
            bounded: false,
            isStdoutWrittenMoreThanOnce: false,
        };
        const onBell = this._eventHandlers.onBell;

        if (onBell) {
            newTab.xterm.onBell(() => onBell(newTab.reqKey));
        }

        if (this._eventHandlers.onNewTab) {
            this._eventHandlers.onNewTab(newTab);
        }

        const webglAddon = newTab.webglAddon;
        webglAddon.onContextLoss((e) => {
            console.log('webgl context lost', e);
            webglAddon.dispose();
        })

        this._states.tabs = [...this._states.tabs, newTab];
        this._states.activeTab = newTab;
    };

    removeTabByTabKey(tabKey: number) {
        const tabIndex = this._states.tabs.findIndex((t) => t.tabKey === tabKey);

        if (tabIndex !== -1) {
            const tab = this._states.tabs[tabIndex];
            tab.xterm.dispose();

            const updatedTabs = [...this._states.tabs];
            updatedTabs.splice(tabIndex, 1);
            this._states.tabs = updatedTabs;

            if (this.activeTab == tab) {
                this.removeAfterActive(tabIndex);
            }
        }

        this._tabMap.delete(tabKey);
    };

    /**
     * 이 Method는 터미널 생성 에러시 호출 된다.
     * @param reqKey
     */
    removeTaByReqKey(reqKey: string) {
        const tabIndex = this._states.tabs.findIndex((t) => t.reqKey === reqKey);

        if (tabIndex !== -1) {
            const tab = this._states.tabs[tabIndex];
            if (tab.xterm)
                tab.xterm.dispose();

            const updatedTabs = [...this._states.tabs];
            updatedTabs.splice(tabIndex, 1);
            this._states.tabs = updatedTabs;

            this.removeAfterActive(tabIndex);
        }

        this._states.lock = false;
    }

    bindTab(reqKey: string, tabKey: number) {
        const tabs = [...this._states.tabs];
        const idx = tabs.findIndex((t) => t.reqKey === reqKey);
        if (idx === -1) {
            console.error(`Tab with key ${reqKey} not found`);
            return;
        }

        const tab = tabs[idx];
        tab.tabKey = tabKey;
        this._states.tabs = tabs;
        this._tabMap.set(tabKey, tab);

        // this.fitAllXTerms();
        this._states.lock = false;
    }

    setBounded(reqKey: string, bounded: boolean) {
        const tabs = [...this._states.tabs];
        const idx = tabs.findIndex((t) => t.reqKey === reqKey);
        if (idx === -1) {
            console.error(`Tab with key ${reqKey} not found`);
            return;
        }
        const tab = tabs[idx];
        tab.bounded = bounded;
        this._states.tabs = tabs;
    }

    writeToStdInByTabKey(tabKey: number, data: string) {
        if (this._eventHandlers.onWriteStdIn) {
            this._eventHandlers.onWriteStdIn(tabKey, data);
        }
    }

    writeStdOutByTabKey(tabKey: number, data: string) {
        const idx = this._states.tabs.findIndex((t) => t.tabKey === tabKey);
        const tab = this._states.tabs[idx];
        tab.isStdoutWrittenMoreThanOnce = true;
        this.extractVisualBell(data).forEach(
            (onBellFlag, eventIdx) => {
                setTimeout(() => {
                    if (this._eventHandlers.onVisualBell) {
                        this._eventHandlers.onVisualBell!(tabKey, onBellFlag);
                    }

                    const options = tab.xterm.options;
                    options.theme = onBellFlag ? this._themeSet.invertTheme : this._themeSet.defaultTheme;
                }, eventIdx * 250);
            }
        );
        tab.xterm.write(data);

    }

    extractVisualBell(data: string) {
        return data.match(VISUAL_BELL_PATTERN)?.map(
            (m) => m.slice(-1) === 'h'
        ) ?? [];
    }

    setTabTitleByTabKey(tabKey: number, title: string) {
        const tabs = [...this._states.tabs];
        const idx = tabs.findIndex((t) => t.tabKey === tabKey);
        if (idx === -1) {
            console.error(`Tab with key ${tabKey} not found`);
            return;
        }
        tabs[idx].title = title;
        this._states.tabs = tabs;
        this._tabMap.set(tabKey, tabs[idx]);
        this.fitActiveTerminal();
    }

    closeTab(key: number) {
        if (this._eventHandlers.onCloseTab) {
            this._eventHandlers.onCloseTab(key);
        }
    }

    fitActiveTerminal() {
        this._states.fitActiveTerminal();
    }

    pasteToActiveTab(content: string | undefined) {
        if (!content) return;
        this.activeTab?.xterm.paste(content);
    }

    get activeTabSelection(): string | undefined {
        return this.activeTab?.xterm.getSelection().toString();
    }

    findNext(text: string, opts?: Omit<ISearchOptions, "decorations">) {
        return this.activeTab?.xtermSearchAddon.findNext(text, {
            decorations: this._themeSet.searchDecorationOptions,
            incremental: false,
            ...opts
        });
    }

    findPrevious(text: string, opts?: Omit<ISearchOptions, "decorations">) {
        return this.activeTab?.xtermSearchAddon.findPrevious(text, {
            decorations: this._themeSet.searchDecorationOptions,
            ...opts
        });
    }

    clearSearchResult() {
        this.activeTab?.xtermSearchAddon.clearDecorations();
        this.activeTab?.xtermSearchAddon.clearActiveDecoration();
    }

    private removeAfterActive(tabIndex: number) {
        if (tabIndex == 0) {
            this.activeTab = this.tabs[1];
        } else if (tabIndex >= this.tabs.length - 1) {
            this.activeTab = this.tabs[tabIndex - 1];
        } else {
            const nextTab = this.tabs[tabIndex + 1];
            const prevTab = this.tabs[tabIndex - 1];

            if (nextTab)
                this.activeTab = nextTab;
            else if (prevTab)
                this.activeTab = prevTab;
        }
    }

    showSearchDialog() {
        const terminalSearchDialog = this.terminalSearchDialogRef.current;
        const activeTabElement = this.activeTab?.xterm.element;

        if (terminalSearchDialog && activeTabElement) {
            const rect = activeTabElement.getBoundingClientRect();
            const x = rect.width / 2;
            const y = rect.height / 2.4;
            this.terminalSearchDialogRef.current.show(x > 0 ? x : 0, y > 0 ? y : 0);
        }
    }

    closeSearchDialog() {
        if (this.terminalSearchDialogRef.current) {
            this.terminalSearchDialogRef.current.close();
        }
    }
}


const TerminalContainerContext = createContext<TerminalScreenContainerContextImpl | null>(null);
export default TerminalContainerContext;